import type { ReactNode } from 'react';
import { Flex } from '@customerio/pluma-components/react';
import { getFilterVisibility, parseFixedFilters, useDeliveryFilterActions, useSearchModeState } from '../hooks';
import type { DeliveryFilterValues } from '../types';
import { ALL_DELIVERY_CHANNEL_VALUES } from '../utils/state-options';
import { FilterRow } from './filter-row';
import { FiltersToggleButton } from './filters-toggle-button';
import { PrimaryFilterRow } from './primary-filter-row';
import { SearchByToggle } from './search-by-toggle';

export type DeliveryFiltersProps = {
  /** The controlled filter values (owned by the page, typically in the URL). */
  values: DeliveryFilterValues;
  /** Called with a patch whenever any filter changes. */
  onChange: (patch: Partial<DeliveryFilterValues>) => void;
  /** Available channel values shown in the channel dropdown. Default: all. */
  channels?: readonly string[];
  /** Drafts view — hides newsletters and narrows the message list to drafts. */
  drafted?: boolean;
  /** Comma-separated locked/hidden filters (`campaign,action,recipient,deliveryStatus,userActions,startTs`). */
  fixed?: string;
  /** Enable the "email address" search toggle (email channel only). */
  isFilteringByEmailEnabled?: boolean;
  /** Disable every control (e.g. while the list is loading). */
  isDisabled?: boolean;
  maxRange?: string | number;
  dateRangeTooltipMessage?: string;
  canClearTimeRange?: boolean;
  /** Called in addition to the value patch when the filter row is cleared. */
  onClearFilters?: () => void;
  /** Trailing toolbar actions rendered at the right of the primary row (e.g. an export button). */
  actions?: ReactNode;
};

/**
 * The Outbox delivery filter bar (FPS-2588). A controlled component: the page
 * owns the `values` and receives a patch on every change; the bar keeps only
 * the transient search-mode UI state locally.
 *
 * Ported from the Ember `<cio-deliveries>` toolbar + `<cio-deliveries/filters>`
 * sub-component: channel select, grouped status select, date-range picker, a
 * "find by filters / email address" toggle, and the campaign/action or
 * email-recipient filter row. The list/table, bulk actions, and export button
 * are out of scope (later Outbox sub-issues).
 */
export function DeliveryFilters({
  values,
  onChange,
  channels = ALL_DELIVERY_CHANNEL_VALUES,
  drafted = false,
  fixed,
  isFilteringByEmailEnabled = false,
  isDisabled = false,
  maxRange = '1 year',
  dateRangeTooltipMessage,
  canClearTimeRange = false,
  onClearFilters,
  actions,
}: DeliveryFiltersProps) {
  const fixedFilters = parseFixedFilters(fixed);
  const { showFilters, setShowFilters, searchBy, setSearchBy } = useSearchModeState(
    values,
    isFilteringByEmailEnabled,
    fixedFilters,
  );
  const filterActions = useDeliveryFilterActions({
    onChange,
    onClearFilters,
    showFilters,
    setShowFilters,
    setSearchBy,
  });
  const { hasChannel, isEmailChannel, showStatusFilter, showFindByToggle, showFiltersToggle } = getFilterVisibility(
    values,
    fixedFilters,
    isFilteringByEmailEnabled,
  );

  const showFilterRow = hasChannel && showFilters;

  const primaryRow = (
    <PrimaryFilterRow
      values={values}
      channels={channels}
      isDisabled={isDisabled}
      searchByEmail={searchBy === 'email'}
      withStatusFilter={showStatusFilter}
      withDateRange={!fixedFilters.has('startTs')}
      canClearTimeRange={canClearTimeRange}
      maxRange={maxRange}
      dateRangeTooltipMessage={dateRangeTooltipMessage}
      onChannelChange={filterActions.onChannelChange}
      onStatusChange={filterActions.onStatusChange}
      onDateChange={onChange}
    />
  );

  return (
    <Flex direction="column" gap="150" w="full">
      {actions == null ? (
        primaryRow
      ) : (
        <Flex justifyContent="space-between" alignItems="center" gap="150" w="full">
          {primaryRow}
          {actions}
        </Flex>
      )}

      {showFindByToggle ? <SearchByToggle searchBy={searchBy} onToggle={filterActions.toggleSearchBy} /> : null}

      {showFilterRow ? (
        <FilterRow
          searchBy={searchBy}
          isEmailChannel={isEmailChannel}
          values={values}
          drafted={drafted}
          fixed={fixedFilters}
          isDisabled={isDisabled}
          onCampaignChange={filterActions.onCampaignChange}
          onActionChange={filterActions.onActionChange}
          onRecipientChange={filterActions.onRecipientChange}
        />
      ) : null}

      {showFiltersToggle ? (
        <FiltersToggleButton showFilters={showFilters} onClick={filterActions.toggleFilters} />
      ) : null}
    </Flex>
  );
}
